"use client";

import type { Id } from "@convex/_generated/dataModel";
import Image from "next/image";
import NextLink from "next/link";
import { formatNumber } from "@/lib/utils";

const MAX_CHIPS = 4;

interface WeaponBuildCardProps {
  build: {
    _id: Id<"weaponBuilds">;
    _creationTime: number;
    name: string;
    description?: string;
    slug: string;
    user: {
      _id: Id<"users">;
      name: string;
      pictureUrl?: string;
      username?: string;
    };
    weapon: {
      _id: Id<"weapons">;
      name: string;
      category: string;
      logoUrl: string | null;
      imageUrl: string | null;
    };
    attachments: Array<{
      _id: Id<"attachments">;
      name: string;
      slotType: string;
      cost: number;
      logoUrl: string | null;
    }>;
    stats: {
      upvotes: number;
      downvotes: number;
      views: number;
      comments: number;
    };
  };
}

export function WeaponBuildCard({ build }: WeaponBuildCardProps) {
  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  const extra = build.attachments.length - MAX_CHIPS;

  return (
    <NextLink
      className="group relative block overflow-hidden rounded-[8px] border border-[#262A2D] bg-[#141618] transition-colors hover:border-[#F5A23E]/50"
      href={`/weapons/builds/${build.slug}`}
    >
      <div className="h-[3px] bg-gradient-to-r from-[#F5A23E] to-transparent" />

      {/* Background weapon image */}
      {build.weapon.imageUrl ? (
        <div className="pointer-events-none absolute inset-0 top-[3px] opacity-[0.08] transition-opacity duration-300 group-hover:opacity-[0.15]">
          <Image
            alt={build.weapon.name}
            className="h-full w-full object-contain object-center"
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            src={build.weapon.imageUrl}
          />
        </div>
      ) : null}

      <div className="relative p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-2 flex-1 font-bold font-heading text-[#EAEBEC] text-base leading-tight">
            {build.name}
          </h3>
          <span className="shrink-0 rounded-[3px] bg-[#46C8E0] px-1.5 py-0.5 font-bold font-mono text-[#0a0b0c] text-[10px]">
            {build.weapon.category}
          </span>
        </div>
        <div className="mt-1.5 font-mono text-[#8B9297] text-xs tracking-wider">
          {build.weapon.name}
        </div>

        {build.description ? (
          <p className="mt-2 line-clamp-2 text-[#8B9297] text-sm">
            {build.description}
          </p>
        ) : null}

        {build.attachments.length > 0 ? (
          <div className="mt-3.5 flex flex-wrap gap-1.5">
            {build.attachments.slice(0, MAX_CHIPS).map((attachment) => (
              <span
                className="rounded-[3px] border border-[#262A2D] bg-[#0C0D0E] px-1.5 py-0.5 font-mono text-[#A9AFB3] text-[10px]"
                key={attachment._id}
              >
                {attachment.name}
              </span>
            ))}
            {extra > 0 ? (
              <span className="rounded-[3px] border border-[#262A2D] bg-[#0C0D0E] px-1.5 py-0.5 font-mono text-[#6a7075] text-[10px]">
                +{extra}
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="mt-4 flex items-center justify-between border-[#262A2D] border-t pt-3 font-mono text-xs">
          <div className="flex gap-3 text-[#8B9297]">
            <span className="text-[#2ec27e]">
              ▲ {formatNumber(build.stats.upvotes)}
            </span>
            <span>◎ {build.stats.comments}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#2b6cb0] text-[9px] text-white">
              {getInitials(build.user.name)}
            </span>
            <span className="text-[#8B9297] text-[11px]">
              {build.user.username ?? build.user.name}
            </span>
          </div>
        </div>
      </div>
    </NextLink>
  );
}
